import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLotes } from '../api/lotes.api';
import { fetchRanchos } from '../api/rancho.api';
import { resolveEntityName } from '../utils/entity-names';
import { useT } from '../i18n';

/**
 * Resuelve ids (loteId, ranchoId…) a nombres legibles usando las listas que el
 * dashboard ya carga (#58). Comparte queryKey con `useRanchoLotes` (rancho = todos)
 * y con los selectores de rancho, así que normalmente no genera requests nuevos.
 * Los ids `GENERAL#<rancho>` (lote virtual de tareas generales) se muestran como "General".
 */
export function useEntityNames() {
  const t = useT();
  const { data: lotes } = useQuery({ queryKey: ['lotes', null], queryFn: () => fetchLotes(null) });
  const { data: ranchos } = useQuery({ queryKey: ['ranchos'], queryFn: () => fetchRanchos() });

  const loteMap = useMemo(() => new Map((lotes ?? []).map((l) => [l.loteId, l.name])), [lotes]);
  const ranchoMap = useMemo(() => new Map((ranchos ?? []).map((r) => [r.ranchoId, r.name])), [ranchos]);

  return useMemo(() => ({
    /** `hint`: nombre que ya trae el registro del backend (se usa solo si no es un UUID). */
    loteName: (id: string | undefined | null, hint?: string | null) =>
      id?.startsWith('GENERAL#') ? t.tasks.generalLabel : resolveEntityName(id, loteMap, hint),
    ranchoName: (id: string | undefined | null, hint?: string | null) => resolveEntityName(id, ranchoMap, hint),
  }), [loteMap, ranchoMap, t]);
}
